import { useEffect, useRef, useCallback } from "react";
import { useLazySalonAvailability } from "@/lib/apollo/hooks/booking-edge";
import { BookingService } from "@/types/booking";

interface UseAvailabilityPollingOptions {
  salonId: string | null;
  services: BookingService[];
  staffId: string | null;
  selectedDate: string | null; // "YYYY-MM-DD"
  selectedTime: string | null; // "HH:MM"
  enabled: boolean;
  pollingInterval?: number; // Default: 20000ms (20 seconds)
  onSlotUnavailable: () => void;
}

interface UseAvailabilityPollingReturn {
  checkNow: () => Promise<boolean>; // Returns true if slot is still available
  isChecking: boolean;
}

/**
 * Hook to poll availability for a selected time slot.
 * Used to detect when a previously available slot becomes unavailable
 * (e.g., another user booked it).
 *
 * This helps mitigate double-booking race conditions by alerting the user
 * before they proceed to payment if their slot is no longer available.
 */
export function useAvailabilityPolling({
  salonId,
  services,
  staffId,
  selectedDate,
  selectedTime,
  enabled,
  pollingInterval = 20000, // 20 seconds default
  onSlotUnavailable,
}: UseAvailabilityPollingOptions): UseAvailabilityPollingReturn {
  const [getAvailability, { loading: isChecking }] = useLazySalonAvailability();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);

  // Ref to store latest callback to avoid stale closure issues
  const onSlotUnavailableRef = useRef(onSlotUnavailable);
  useEffect(() => {
    onSlotUnavailableRef.current = onSlotUnavailable;
  }, [onSlotUnavailable]);

  /**
   * Check if the selected time slot is still available.
   * Returns true if available, false if not.
   */
  const checkAvailability = useCallback(async (): Promise<boolean> => {
    // Don't check if we don't have all required data
    if (!salonId || services.length === 0 || !selectedDate || !selectedTime) {
      return true; // Assume available if we can't check
    }

    // Prevent concurrent checks
    if (isCheckingRef.current) {
      return true;
    }

    isCheckingRef.current = true;

    try {
      // Build services input for the query
      const servicesInput = services.map((service) => {
        // Extract original service ID if it contains a variant ID
        let originalServiceId = service.id;
        if (service.variantId && service.id.includes("-")) {
          originalServiceId = service.id.slice(
            0,
            -(service.variantId.length + 1)
          );
        }

        let staffIds: string[] | null = null;
        if (service.staffIds?.length) {
          staffIds = service.staffIds;
        } else if (service.staff?.id) {
          staffIds = [service.staff.id];
        } else if (staffId) {
          staffIds = [staffId];
        }

        return {
          serviceId: originalServiceId,
          staffId: staffIds,
          serviceVariantId: service.variantId || null,
        };
      });

      const { data } = await getAvailability({
        salonId,
        services: servicesInput,
        startDate: selectedDate,
        endDate: selectedDate,
      });

      // Check if the selected time is in the available slots
      if (data?.salonAvailability?.length > 0) {
        const allSlots: string[] = [];

        data.salonAvailability.forEach((availability: any) => {
          if (availability.results && availability.results.length > 0) {
            availability.results.forEach((slot: any) => {
              // Convert from HH:MM:SS to HH:MM format
              const time = slot.startTime.slice(0, 5);
              allSlots.push(time);
            });
          }
        });

        // Check if our selected time is still available
        const isStillAvailable = allSlots.includes(selectedTime);

        if (!isStillAvailable) {
          // Slot is no longer available - notify the callback
          onSlotUnavailableRef.current();
          return false;
        }

        return true;
      } else {
        // No availability data returned - slot is unavailable
        onSlotUnavailableRef.current();
        return false;
      }
    } catch (error) {
      console.error("[useAvailabilityPolling] Error checking availability:", error);
      // On error, don't trigger unavailable - let user continue
      // This prevents false positives from network issues
      return true;
    } finally {
      isCheckingRef.current = false;
    }
  }, [salonId, services, staffId, selectedDate, selectedTime, getAvailability]);

  /**
   * Manually trigger an availability check.
   * Useful for checking immediately when returning to a step.
   */
  const checkNow = useCallback(async (): Promise<boolean> => {
    return await checkAvailability();
  }, [checkAvailability]);

  // Set up polling interval
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Don't poll if not enabled or missing required data
    if (!enabled || !salonId || services.length === 0 || !selectedDate || !selectedTime) {
      return;
    }

    // Start polling
    intervalRef.current = setInterval(() => {
      checkAvailability();
    }, pollingInterval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, salonId, services, selectedDate, selectedTime, pollingInterval, checkAvailability]);

  return {
    checkNow,
    isChecking,
  };
}

export default useAvailabilityPolling;
